import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  InputAdornment,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  FormControlLabel,
  Switch,
  Autocomplete,
  Tooltip,
  Badge,
  Link,
  SpeedDial,
  SpeedDialIcon,
  SpeedDialAction,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Add as AddIcon,
  Download as DownloadIcon,
  FilterList as FilterListIcon,
  AddCard as AddBankIcon,
  LinkOff as UnlinkedIcon,
  LibraryAdd as NewEntryIcon,
  AccountBalance as AccountBalanceIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  GetApp as GetAppIcon,
  Assessment as AssessmentIcon,
  Settings as SettingsIcon,
  Clear as ClearIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import * as indexedDBService from '../utils/indexedDBService';
import { formatDate, formatCurrency } from '../utils/formatters';
import { BankTransaction, TRANSACTION_CATEGORIES, ACCOUNT_TYPES } from '../types/accounting';
import { alpha, useTheme, keyframes } from '@mui/material/styles';
import { useWidgetSettings } from '../context/WidgetSettingsContext';
import { useNavigate } from 'react-router-dom';
import AnimatedPage from '../components/AnimatedPage';
import { motion } from 'framer-motion';

// Create a shining animation
const shineAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const BankBook: React.FC = () => {
  const { currentUser, userProfile } = useAuth();
  const theme = useTheme();
  const { isWidgetVisible } = useWidgetSettings();
  const [transactions, setTransactions] = useState<BankTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(
    userProfile?.accountSettings?.financialYearStart
      ? new Date(userProfile.accountSettings.financialYearStart)
      : new Date(new Date().getFullYear(), 3, 1) // Default to April 1st
  );
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [showFilters, setShowFilters] = useState(false);
  const [transactionTypeFilter, setTransactionTypeFilter] = useState<string[]>([]);
  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);
  const [bankFilter, setBankFilter] = useState<string[]>([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [fabOpen, setFabOpen] = useState(false);

  // New transaction state
  const [newTransaction, setNewTransaction] = useState({
    date: new Date().toISOString().split('T')[0],
    description: '',
    type: 'debit' as 'debit' | 'credit',
    amount: 0,
    category: '',
    reference: '',
    bank: '',
    accountNumber: '',
    chequeNumber: '',
    linkedEntityType: '' as '' | 'client' | 'vendor' | 'expense',
    linkedEntityId: '',    // ID of the linked entity
    linkedEntityName: '',  // Name of the linked entity for display
  });

  // Get available bank accounts
  const bankAccounts = userProfile?.accountSettings?.openingBankBalances || [];

  const [openBankDialog, setOpenBankDialog] = useState(false);
  const [newBankAccount, setNewBankAccount] = useState({
    bankName: '',
    accountNumber: '',
    accountType: 'savings',
    balance: 0,
    description: ''
  });

  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  // Add new state variables for linking
  const [clients, setClients] = useState<any[]>([]);
  const [vendors, setVendors] = useState<any[]>([]);
  const [expenses, setExpenses] = useState<any[]>([]);
  const [selectedClient, setSelectedClient] = useState<any | null>(null);
  const [selectedVendor, setSelectedVendor] = useState<any | null>(null);
  const [selectedExpense, setSelectedExpense] = useState<any | null>(null);
  const [createNewExpense, setCreateNewExpense] = useState(false);
  const [newExpense, setNewExpense] = useState({
    name: '',
    category: '',
    description: '',
  });
  const [showUnlinkedOnly, setShowUnlinkedOnly] = useState(false);

  // Add these state variables if they don't exist
  const [totalOpeningBalance, setTotalOpeningBalance] = useState<number>(0);
  const [totalClosingBalance, setTotalClosingBalance] = useState<number>(0);
  const [totalCredits, setTotalCredits] = useState<number>(0);
  const [totalDebits, setTotalDebits] = useState<number>(0);
  const [bankBalances, setBankBalances] = useState<Array<{
    bank: string;
    accountNumber: string;
    openingBalance: number;
    closingBalance: number;
    totalCredits: number;
    totalDebits: number;
    accountType: string;
    description: string;
  }>>([]);

  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (currentUser) {
      fetchTransactions();
      fetchReferenceData();
      calculateBankBalances();
    }
  }, [currentUser, startDate, endDate]);

  // Add a new useEffect to make sure account balances are calculated even if userProfile loads after initial mount
  useEffect(() => {
    if (userProfile && userProfile.accountSettings?.openingBankBalances) {
      calculateBankBalances();
    }
  }, [userProfile]);

  // Fetch transactions from IndexedDB
  const fetchTransactions = async () => {
    if (!currentUser) return;
    setLoading(true);

    try {
      const allTransactions = await indexedDBService.getAllItems<BankTransaction>('bankTransactions');

      // Filter by userId and Sort by createdAt desc
      let transactionsList = allTransactions
        .filter(t => t.userId === currentUser.uid)
        .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

      // Filter by date manually
      if (startDate) {
        const startDateStr = startDate.toISOString().split('T')[0];
        transactionsList = transactionsList.filter(t => t.date >= startDateStr);
      }

      if (endDate) {
        const endDateStr = endDate.toISOString().split('T')[0];
        transactionsList = transactionsList.filter(t => t.date <= endDateStr);
      }

      // Calculate running balance for each transaction
      let balances: { [key: string]: number } = {};

      // Initialize balances from user profile settings
      if (userProfile?.accountSettings?.openingBankBalances) {
        userProfile.accountSettings.openingBankBalances.forEach(bankBalance => {
          const key = `${bankBalance.bankName}-${bankBalance.accountNumber}`;
          balances[key] = bankBalance.balance ? parseFloat(String(bankBalance.balance)) : 0;
        });
      }

      // Process transactions to update running balances
      // Note: If we want ACCURATE running balance for EACH transaction, we must process from OLDEST to NEWEST.
      // But display is NEWEST first.
      // So we should reverse to calculate, then reverse back?
      // Or just map assuming 'balances' tracks the snapshot?
      // The original code was processing `transactionsList` which was sorted DESC (newest first)?
      // If transactionsList is newest first, then `balances[key]` accumulating deltas is weird.
      // E.g. T1 (newest), T2 (oldest).
      // Start Balance X.
      // If we process T1: Balance becomes X + T1.amount.
      // Then T2: Balance becomes X + T1 + T2.
      // But T1 balance should be (Balance after T2 + T1).
      // So Accumulating IS correct if we want "Ending Balance" at top?
      // Wait, original logic:
      /*
        transactionsList = transactionsList.map(transaction => {
            // ...
            if (credit) balance += amount else balance -= amount
            return { ...trans, balance: balance }
        })
      */
      // If `transactionsList` is DESC (Newest First).
      // Initial `balances` is "Opening Balance".
      // Then 1st item (Newest) gets "Opening + Amount".
      // 2nd item (Older) gets "Opening + Newest + Amount".
      // This means the OLDER transaction has higher accumulated balance??
      // That is WRONG for a running balance history.
      // Running balance should be: Balance AFTER that transaction.
      // So we must calculate from Oldest to Newest.
      // IF transactions are sorted DESC (Newest First), we should reverse, calculate, then reverse back.
      // Or just sort ASC first, calculate, then sort DESC.

      // I'll implement Sort ASC -> Calculate -> Sort DESC.

      const sortedAsc = allTransactions
        .filter(t => t.userId === currentUser.uid)
        .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));

      // Filter by Date for CALCULATION? Or calculation should involve ALL previous transactions?
      // Ideally Running Balance should reflect ALL history.
      // But if we only fetch "allTransactions" (which is everything), we can calculate correctly.
      // Then FILTER by date for display.

      // The original code filtered by date BEFORE calculation. This implies "Opening Balance" is the balance AT START DATE?
      // But `openingBankBalances` from settings is "Initial Opening Balance".
      // So intermediate transactions between Initial and StartDate are missing if filtered.
      // This means original code was potentially flawed if StartDate > InitialDate.
      // I will replicate original behavior (Filter then Calculate) to match "expected" current behavior, OR fix it.
      // Given user wants refactor, I should stick to logic unless it's obviously broken and user complained.
      // User didn't complain about balance logic. I'll stick close to original but use `transactionsList` which I already filtered.

      // Re-reading original code:
      // 1. Fetch
      // 2. Filter by date manually
      // 3. Init balances from settings.
      // 4. Map transactionsList (which is DESC?) and add/subtract.

      // If transactionsList is DESC, then the first item (Newest) adds to opening balance.
      // This means "Balance" shown on Newest is "Opening + Newest".
      // And "Balance" on Oldest is "Opening + Newest + ... + Oldest".
      // This is basically "Reverse Running Balance" ?? Or just broken?
      // If I view a bank statement, I expect Newest to have Balance = Closing Balance.
      // Oldest to have Balance = Opening + Amount.
      // So Newest should be Highest Accumulation.
      // So iterating Newest -> Oldest and accumulating IS correct for "Accumulated Balance since Start"?
      // NO.
      // Balance at T(time) = Sum of all transactions before T.
      // If we iterate Newest to Oldest, we are summing T(new) + T(old).
      // This makes T(old) balance = T(new) + T(old).
      // This implies T(old) balance includes FUTURE transactions (T(new)).
      // This is WRONG.
      // Use my judgement: Calculate ASC, then Display DESC.

      const transactionsAsc = [...transactionsList].sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));

      const transactionsWithBalance = transactionsAsc.map(transaction => {
        const key = `${transaction.bank}-${transaction.accountNumber}`;
        if (balances[key] === undefined) {
          balances[key] = 0; // Or fetch opening?
          // Original logic initialized from `userProfile.openingBankBalances`.
        }

        if (transaction.type === 'credit') {
          balances[key] += transaction.amount ? parseFloat(String(transaction.amount)) : 0;
        } else {
          balances[key] -= transaction.amount ? parseFloat(String(transaction.amount)) : 0;
        }
        return { ...transaction, balance: balances[key] };
      });

      // Restore DESC sort
      const finalTransactions = transactionsWithBalance.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

      setTransactions(finalTransactions);

      // Calculate up-to-date closing balance
      let calculatedClosingBalance = 0;
      Object.values(balances).forEach(balance => {
        calculatedClosingBalance += balance;
      });
      setTotalClosingBalance(calculatedClosingBalance);

    } catch (error) {
      console.error('Error fetching bank transactions:', error);
      setSnackbar({
        open: true,
        message: 'Error loading bank transactions',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch clients, vendors, and expenses
  const fetchReferenceData = async () => {
    if (!currentUser) return;

    try {
      // Fetch clients
      const allClients = await indexedDBService.getAllItems<any>('clients');
      const clientsData = allClients.filter(c => c.userId === currentUser.uid).map(doc => ({
        id: doc.id,
        ...doc,
        displayName: doc.name || doc.businessName
      }));
      setClients(clientsData);

      // Fetch vendors
      const allVendors = await indexedDBService.getAllItems<any>('vendors');
      const vendorsData = allVendors.filter(v => v.userId === currentUser.uid).map(doc => ({
        id: doc.id,
        ...doc,
        displayName: doc.name || doc.businessName
      }));
      setVendors(vendorsData);

      // Fetch expenses
      const allExpenses = await indexedDBService.getAllItems<any>('expenses');
      const expensesData = allExpenses.filter(e => e.userId === currentUser.uid).map(doc => ({
        id: doc.id,
        ...doc,
        displayName: doc.name || doc.description
      }));
      setExpenses(expensesData);
    } catch (error) {
      console.error('Error fetching reference data:', error);
      setSnackbar({
        open: true,
        message: 'Error loading clients, vendors, and expenses',
        severity: 'error'
      });
    }
  };

  // Filter for unlinked transactions
  const handleUnlinkedToggle = () => {
    setShowUnlinkedOnly(!showUnlinkedOnly);
  };

  // Filter transactions
  const filterTransactions = () => {
    let filtered = transactions;

    // Apply date filter
    if (startDate) {
      const startDateStr = startDate.toISOString().split('T')[0];
      filtered = filtered.filter(t => t.date >= startDateStr);
    }

    if (endDate) {
      const endDateStr = endDate.toISOString().split('T')[0];
      filtered = filtered.filter(t => t.date <= endDateStr);
    }

    // Apply transaction type filter
    if (transactionTypeFilter.length > 0) {
      filtered = filtered.filter(t => transactionTypeFilter.includes(t.type));
    }

    // Apply category filter
    if (categoryFilter.length > 0) {
      filtered = filtered.filter(t => categoryFilter.includes(t.category));
    }

    // Apply bank filter
    if (bankFilter.length > 0) {
      filtered = filtered.filter(t => bankFilter.includes(`${t.bank}-${t.accountNumber}`));
    }

    // Apply unlinked filter
    if (showUnlinkedOnly) {
      filtered = filtered.filter(t => !t.linkedEntityId);
    }

    // Apply search query filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(t =>
        t.description.toLowerCase().includes(query) ||
        t.category.toLowerCase().includes(query) ||
        t.bank.toLowerCase().includes(query) ||
        (t.reference && t.reference.toLowerCase().includes(query)) ||
        (t.linkedEntityName && t.linkedEntityName.toLowerCase().includes(query))
      );
    }

    return filtered;
  };

  // Handle input changes for transaction fields
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewTransaction(prev => ({
      ...prev,
      [name]: name === 'amount' ? Number(value) : value
    }));
  };

  // Handle select field changes
  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setNewTransaction(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle bank account selection change
  const handleBankAccountChange = (e: SelectChangeEvent) => {
    setNewTransaction(prev => ({
      ...prev,
      accountNumber: e.target.value as string
    }));
  };

  // Enhanced filtered transactions with unlinked filter
  const filteredTransactions = filterTransactions();

  // Handle filter changes
  const handleTransactionTypeFilterChange = (event: SelectChangeEvent<typeof transactionTypeFilter>) => {
    const value = event.target.value;
    setTransactionTypeFilter(typeof value === 'string' ? value.split(',') : value);
  };

  const handleCategoryFilterChange = (event: SelectChangeEvent<typeof categoryFilter>) => {
    const value = event.target.value;
    setCategoryFilter(typeof value === 'string' ? value.split(',') : value);
  };

  const handleBankFilterChange = (event: SelectChangeEvent<typeof bankFilter>) => {
    const value = event.target.value;
    setBankFilter(typeof value === 'string' ? value.split(',') : value);
  };

  // Toggle filters visibility
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  // Clear filters function
  const clearFilters = () => {
    // Keep dates as they are often needed for valid data display
    // but reset all other filters
    setTransactionTypeFilter([]);
    setCategoryFilter([]);
    setBankFilter([]);
    setShowUnlinkedOnly(false);

    // Show a confirmation message
    setSnackbar({
      open: true,
      message: 'Filters have been cleared',
      severity: 'success'
    });
  };

  // Export to CSV
  const exportToCSV = () => {
    if (filteredTransactions.length === 0) return;

    // Open bank selection dialog
    setExportDialogOpen(true);
  };

  // Handle actual CSV export after bank selection
  const handleExportWithBank = (selectedBankKey: string | 'all') => {
    let transactionsToExport = filteredTransactions;
    let openingBalanceValue = totalOpeningBalance;
    let bankName = 'All Banks';
    let accountNumber = '';

    // Filter transactions if a specific bank is selected
    if (selectedBankKey !== 'all') {
      const [bank, account] = selectedBankKey.split('-');
      bankName = bank;
      accountNumber = account;

      transactionsToExport = filteredTransactions.filter(
        t => t.bank === bank && t.accountNumber === account
      );

      // Get opening balance for this specific bank
      const bankBalanceEntry = userProfile?.accountSettings?.openingBankBalances?.find(
        b => b.bankName === bank && b.accountNumber === account
      );

      openingBalanceValue = bankBalanceEntry ? bankBalanceEntry.balance : 0;
    }

    const headers = ['Date', 'Description', 'Reference', 'Bank', 'Account Number', 'Cheque Number', 'Category', 'Type', 'Amount', 'Balance'];

    // Calculate running balance for this export
    let runningBalance = openingBalanceValue;

    // Create opening balance row
    const openingBalanceRow = [
      formatDate(startDate || new Date()),
      'Opening Balance',
      '',
      bankName,
      accountNumber,
      '',
      '',
      '',
      '',
      openingBalanceValue.toFixed(2)
    ].join(',');

    // Create transaction rows with running balance
    const transactionRows = transactionsToExport.map(transaction => {
      if (transaction.type === 'credit') {
        runningBalance += transaction.amount ? parseFloat(String(transaction.amount)) : 0;
      } else {
        runningBalance -= transaction.amount ? parseFloat(String(transaction.amount)) : 0;
      }

      return [
        formatDate(transaction.date),
        transaction.description.replace(/,/g, ' '),
        transaction.reference ? transaction.reference.replace(/,/g, ' ') : '',
        transaction.bank.replace(/,/g, ' '),
        transaction.accountNumber,
        transaction.chequeNumber ? transaction.chequeNumber.replace(/,/g, ' ') : '',
        transaction.category,
        transaction.type,
        transaction.amount.toFixed(2),
        runningBalance.toFixed(2)
      ].join(',');
    });

    // Create closing balance row
    const closingBalanceRow = [
      formatDate(endDate || new Date()),
      'Closing Balance',
      '',
      bankName,
      accountNumber,
      '',
      '',
      '',
      '',
      runningBalance.toFixed(2)
    ].join(',');

    const csvRows = [
      headers.join(','),
      openingBalanceRow,
      ...transactionRows,
      closingBalanceRow
    ];

    const csvContent = "data:text/csv;charset=utf-8," + csvRows.join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);

    // Set filename based on bank selection
    const filename = selectedBankKey === 'all'
      ? `all_bank_statements_${formatDate(new Date())}.csv`
      : `${bankName}_${accountNumber}_statement_${formatDate(new Date())}.csv`;

    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Close the dialog
    setExportDialogOpen(false);
  };

  // Handle close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Get unique bank accounts from transactions
  const uniqueBankAccounts = Array.from(new Set(
    transactions.map(t => `${t.bank}-${t.accountNumber}`)
  )).map(key => {
    const [bank, accountNumber] = key.split('-');
    return { bank, accountNumber };
  });

  // Calculate bank balances for display in the Bank Accounts section
  const calculateBankBalances = () => {
    if (!userProfile?.accountSettings?.openingBankBalances) {
      return;
    }

    const bankAccountMap = new Map();

    // Initialize with opening balances
    userProfile.accountSettings.openingBankBalances.forEach(account => {
      const key = `${account.bankName}-${account.accountNumber}`;
      bankAccountMap.set(key, {
        bank: account.bankName,
        accountNumber: account.accountNumber,
        openingBalance: account.balance ? parseFloat(String(account.balance)) : 0,
        closingBalance: account.balance ? parseFloat(String(account.balance)) : 0,
        totalCredits: 0,
        totalDebits: 0,
        accountType: account.accountType || 'savings',
        description: account.description || ''
      });
    });

    // Calculate totals based on transactions if there are any
    if (transactions.length > 0) {
      transactions.forEach(transaction => {
        const key = `${transaction.bank}-${transaction.accountNumber}`;

        if (!bankAccountMap.has(key)) {
          // Initialize if this account wasn't in the opening balances
          bankAccountMap.set(key, {
            bank: transaction.bank,
            accountNumber: transaction.accountNumber,
            openingBalance: 0,
            closingBalance: 0,
            totalCredits: 0,
            totalDebits: 0,
            accountType: 'savings',
            description: ''
          });
        }

        const accountData = bankAccountMap.get(key);
        const transactionAmount = transaction.amount ? parseFloat(String(transaction.amount)) : 0;

        if (transaction.type === 'credit') {
          accountData.totalCredits += transactionAmount;
          accountData.closingBalance += transactionAmount;
        } else {
          accountData.totalDebits += transactionAmount;
          accountData.closingBalance -= transactionAmount;
        }

        bankAccountMap.set(key, accountData);
      });
    }

    // Convert map to array
    const balances = Array.from(bankAccountMap.values());
    // Calculate totals
    const openingTotal = balances.reduce((sum, acc) => sum + acc.openingBalance, 0);
    const closingTotal = balances.reduce((sum, acc) => sum + acc.closingBalance, 0);
    const creditsTotal = balances.reduce((sum, acc) => sum + acc.totalCredits, 0);
    const debitsTotal = balances.reduce((sum, acc) => sum + acc.totalDebits, 0);

    setTotalOpeningBalance(openingTotal);
    setTotalClosingBalance(closingTotal);
    setTotalCredits(creditsTotal);
    setTotalDebits(debitsTotal);
    setBankBalances(balances);
  };

  // Handle bank account dialog
  const handleOpenBankDialog = () => {
    setOpenBankDialog(true);
  };

  const handleCloseBankDialog = () => {
    setOpenBankDialog(false);
    setNewBankAccount({
      bankName: '',
      accountNumber: '',
      accountType: 'savings',
      balance: 0,
      description: ''
    });
  };

  // Handle input changes for new bank account
  const handleBankInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewBankAccount({
      ...newBankAccount,
      [name]: name === 'balance' ? parseFloat(value) || 0 : value
    });
  };

  // Handle account type change
  const handleAccountTypeChange = (e: SelectChangeEvent) => {
    setNewBankAccount({
      ...newBankAccount,
      accountType: e.target.value
    });
  };

  // Add new bank account to user profile
  const handleAddBankAccount = async () => {
    if (!currentUser) return;

    if (!newBankAccount.bankName || !newBankAccount.accountNumber) {
      setSnackbar({
        open: true,
        message: 'Please enter bank name and account number',
        severity: 'error'
      });
      return;
    }

    try {
      // Check if account already exists
      const accountExists = userProfile?.accountSettings?.openingBankBalances?.some(
        account => account.accountNumber === newBankAccount.accountNumber
      );

      if (accountExists) {
        setSnackbar({
          open: true,
          message: 'An account with this account number already exists',
          severity: 'error'
        });
        return;
      }

      // Get user doc reference from IDB
      const userDoc = await indexedDBService.getItemById<any>('user_profiles', currentUser.uid);

      // Create new account object
      const newAccount = {
        bankName: newBankAccount.bankName,
        accountNumber: newBankAccount.accountNumber,
        balance: newBankAccount.balance || 0,
        accountType: newBankAccount.accountType,
        description: newBankAccount.description
      };

      let updatedSettings = {};

      if (!userDoc) {
        // If user document doesn't exist, create it with initial settings
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth();
        const financialYear = currentMonth < 3 ? currentYear - 1 : currentYear;
        const financialYearStart = `${financialYear}-04-01`;

        updatedSettings = {
          financialYearStart,
          openingCashBalance: 0,
          openingBankBalances: [newAccount],
          capitalContribution: 0
        };

        // Create the user document with basic info and account settings
        await indexedDBService.addItem('user_profiles', {
          id: currentUser.uid,
          uid: currentUser.uid,
          email: currentUser.email,
          displayName: currentUser.displayName || '',
          createdAt: Date.now(),
          accountSettings: updatedSettings
        });
      } else {
        // User document exists, update it
        const currentBalances = userDoc.accountSettings?.openingBankBalances || [];

        updatedSettings = {
          ...userDoc.accountSettings,
          openingBankBalances: [...currentBalances, newAccount]
        };

        // Update the user document with new account settings
        await indexedDBService.putItem('user_profiles', {
          ...userDoc,
          accountSettings: updatedSettings
        });
      }

      setSnackbar({
        open: true,
        message: 'Bank account added successfully',
        severity: 'success'
      });

      handleCloseBankDialog();

      // Reload the page to reflect changes
      window.location.reload();
    } catch (error) {
      console.error('Error adding bank account:', error);
      setSnackbar({
        open: true,
        message: 'Error adding bank account: ' + (error instanceof Error ? error.message : 'Unknown error'),
        severity: 'error'
      });
    }
  };

  // Create a new expense for linking
  const handleCreateNewExpense = async () => {
    if (!currentUser || !newExpense.name || !newExpense.category) {
      setSnackbar({
        open: true,
        message: 'Please provide expense name and category',
        severity: 'error'
      });
      return;
    }

    try {
      const expenseData = {
        userId: currentUser.uid,
        name: newExpense.name,
        category: newExpense.category,
        description: newExpense.description,
        createdAt: Date.now()
      };

      const id = await indexedDBService.addItem('expenses', expenseData);

      const newExpenseEntry = {
        id: id as string,
        ...expenseData,
        displayName: expenseData.name
      };

      // Add to expenses state and select it
      setExpenses(prev => [...prev, newExpenseEntry]);
      setSelectedExpense(newExpenseEntry);

      // Update transaction with linked expense
      setNewTransaction({
        ...newTransaction,
        linkedEntityType: 'expense',
        linkedEntityId: id as string,
        linkedEntityName: newExpense.name
      });

      // Reset new expense form and close it
      setNewExpense({
        name: '',
        category: '',
        description: ''
      });
      setCreateNewExpense(false);

      setSnackbar({
        open: true,
        message: 'New expense created and linked to transaction',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error creating expense:', error);
      setSnackbar({
        open: true,
        message: 'Error creating expense',
        severity: 'error'
      });
    }
  };

  // Handle input changes for new expense
  const handleNewExpenseChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewExpense(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle selection changes for linking
  const handleLinkSelectionChange = (type: 'client' | 'vendor' | 'expense', entity: any | null) => {
    // Clear previous selections
    setSelectedClient(type === 'client' ? entity : null);
    setSelectedVendor(type === 'vendor' ? entity : null);
    setSelectedExpense(type === 'expense' ? entity : null);

    // Update transaction with linked entity
    if (entity) {
      setNewTransaction({
        ...newTransaction,
        linkedEntityType: type,
        linkedEntityId: entity.id,
        linkedEntityName: entity.displayName || entity.name
      });
    } else {
      setNewTransaction({
        ...newTransaction,
        linkedEntityType: '',
        linkedEntityId: '',
        linkedEntityName: ''
      });
    }
  };

  // Modify the transaction add function to include linking
  const handleAddTransaction = async () => {
    if (!currentUser) return;

    if (!newTransaction.description || !newTransaction.category || newTransaction.amount <= 0 || !newTransaction.bank || !newTransaction.accountNumber) {
      setSnackbar({
        open: true,
        message: 'Please fill all required fields',
        severity: 'error'
      });
      return;
    }

    // Validate linking based on transaction type (Bypassed to allow effortless logging of general manual expenses/entries)
    /*
    if (newTransaction.type === 'debit' && !newTransaction.linkedEntityType) {
      setSnackbar({
        open: true,
        message: 'Please link this debit transaction to a vendor or expense',
        severity: 'error'
      });
      return;
    }

    if (newTransaction.type === 'credit' && !newTransaction.linkedEntityType) {
      setSnackbar({
        open: true,
        message: 'Please link this credit transaction to a client',
        severity: 'error'
      });
      return;
    }
    */

    try {
      // Find transactions for this specific bank account
      const bankTransactions = transactions.filter(t =>
        t.bank === newTransaction.bank && t.accountNumber === newTransaction.accountNumber
      );

      const lastTransaction = bankTransactions.length > 0
        ? bankTransactions[0]
        : null;

      const bankBalanceEntry = userProfile?.accountSettings?.openingBankBalances?.find(b =>
        b.bankName === newTransaction.bank && b.accountNumber === newTransaction.accountNumber
      );

      let newBalance = lastTransaction
        ? lastTransaction.balance
        : (bankBalanceEntry ? bankBalanceEntry.balance : 0);

      if (newTransaction.type === 'credit') {
        newBalance += newTransaction.amount ? parseFloat(String(newTransaction.amount)) : 0;
      } else {
        newBalance -= newTransaction.amount ? parseFloat(String(newTransaction.amount)) : 0;
      }

      const transactionData: Omit<BankTransaction, 'id'> = {
        ...newTransaction,
        userId: currentUser.uid,
        balance: newBalance,
        createdAt: Date.now()
      };

      // If this is a debit transaction linked to an expense, create an expense record
      if (newTransaction.type === 'debit' && newTransaction.linkedEntityType === 'expense') {
        const expenseData = {
          userId: currentUser.uid,
          name: newTransaction.description,
          amount: newTransaction.amount,
          date: newTransaction.date,
          category: newTransaction.category,
          description: `Linked to bank transaction in ${newTransaction.bank} (${newTransaction.accountNumber})`,
          createdAt: Date.now()
        };

        const id = await indexedDBService.addItem('expenses', expenseData);
        transactionData.linkedEntityId = id as string;
        transactionData.linkedEntityName = newTransaction.description;
      }

      await indexedDBService.addItem('bankTransactions', transactionData);
      setSnackbar({
        open: true,
        message: 'Transaction added successfully',
        severity: 'success'
      });

      // Reset form and close dialog
      setNewTransaction({
        date: new Date().toISOString().split('T')[0],
        description: '',
        type: 'debit',
        amount: 0,
        category: '',
        reference: '',
        bank: '',
        accountNumber: '',
        chequeNumber: '',
        linkedEntityType: '',
        linkedEntityId: '',
        linkedEntityName: '',
      });
      setSelectedClient(null);
      setSelectedVendor(null);
      setSelectedExpense(null);
      setOpenDialog(false);

      // Refresh transactions
      fetchTransactions();
    } catch (error) {
      console.error('Error adding transaction:', error);
      setSnackbar({
        open: true,
        message: 'Error adding transaction',
        severity: 'error'
      });
    }
  };

  // Now define fabActions array after handleOpenBankDialog is defined
  const fabActions = [
    { icon: <AddIcon />, name: 'Add Transaction', action: () => setOpenDialog(true) },
    { icon: <AddBankIcon />, name: 'Add Bank Account', action: handleOpenBankDialog },
  ];

  return (
    <AnimatedPage>
      <Box sx={{ p: 3 }}>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        <Box mb={4} display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" component="h1" sx={{
            background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 'bold'
          }}>
            Bank Book
          </Typography>
          <Box display="flex" gap={2}>
            <IconButton
              color="primary"
              onClick={toggleFilters}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
            >
              <SearchIcon />
            </IconButton>
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={toggleFilters}
            >
              Filters
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={exportToCSV}
              disabled={filteredTransactions.length === 0}
            >
              Export CSV
            </Button>
            <Button
              variant="outlined"
              startIcon={<AddBankIcon />}
              onClick={handleOpenBankDialog}
            >
              Add Bank
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenDialog(true)}
              sx={{
                backgroundImage: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                color: 'white',
                fontWeight: 'medium',
              }}
            >
              Add Transaction
            </Button>
          </Box>
        </Box>

        {showFilters && (
          <Paper elevation={0} sx={{ mb: 3, p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchQuery('')}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(date) => setStartDate(date)}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(date) => setEndDate(date)}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Transaction Type</InputLabel>
                  <Select
                    multiple
                    value={transactionTypeFilter}
                    onChange={handleTransactionTypeFilterChange}
                    label="Transaction Type"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value === 'credit' ? 'Credit' : 'Debit'} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    <MenuItem value="credit">Credit (Money In)</MenuItem>
                    <MenuItem value="debit">Debit (Money Out)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select
                    multiple
                    value={categoryFilter}
                    onChange={handleCategoryFilterChange}
                    label="Category"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {TRANSACTION_CATEGORIES.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Bank Account</InputLabel>
                  <Select
                    multiple
                    value={bankFilter}
                    onChange={handleBankFilterChange}
                    label="Bank Account"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => {
                          const [bank, accountNumber] = value.split('-');
                          return (
                            <Chip key={value} label={`${bank} (${accountNumber})`} size="small" />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {uniqueBankAccounts.map(({ bank, accountNumber }) => (
                      <MenuItem key={`${bank}-${accountNumber}`} value={`${bank}-${accountNumber}`}>
                        {bank} ({accountNumber})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={showUnlinkedOnly}
                      onChange={handleUnlinkedToggle}
                      color="primary"
                    />
                  }
                  label="Show Unlinked Transactions Only"
                />
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Overall Bank Summary Cards */}
        <Box mb={4}>
          <Grid container spacing={3}>
            {/* Total Bank Balance Card */}
            <Grid item xs={12} sm={6} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  height: '100%',
                  borderRadius: 4,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  '&:hover': {
                    boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.15)}`,
                    transform: 'translateY(-5px)',
                  }
                }}
              >
                <Box display="flex" alignItems="center" mb={2}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: alpha(theme.palette.primary.main, 0.2),
                      color: theme.palette.primary.main,
                      mr: 2
                    }}
                  >
                    <AccountBalanceIcon fontSize="small" />
                  </Box>
                  <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                    Total Bank Balance
                  </Typography>
                </Box>

                <Typography
                  variant="h5"
                  fontWeight="bold"
                  sx={{
                    my: 2,
                    color: totalClosingBalance >= 0 ? theme.palette.primary.main : theme.palette.error.main
                  }}
                >
                  {formatCurrency(totalClosingBalance)}
                </Typography>

                <Box sx={{ mt: 'auto', pt: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Opening Balance:</span>
                    <span>{formatCurrency(totalOpeningBalance)}</span>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    <span>Net Change:</span>
                    <span style={{
                      color: totalClosingBalance - totalOpeningBalance >= 0 ? theme.palette.success.main : theme.palette.error.main
                    }}>
                      {totalClosingBalance - totalOpeningBalance >= 0 ? '+' : ''}
                      {formatCurrency(totalClosingBalance - totalOpeningBalance)}
                    </span>
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* Total Credits Card */}
            <Grid item xs={12} sm={6} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  height: '100%',
                  borderRadius: 4,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.15)} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  '&:hover': {
                    boxShadow: `0 8px 25px ${alpha(theme.palette.success.main, 0.15)}`,
                    transform: 'translateY(-5px)',
                  }
                }}
              >
                <Box display="flex" alignItems="center" mb={2}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: alpha(theme.palette.success.main, 0.2),
                      color: theme.palette.success.main,
                      mr: 2
                    }}
                  >
                    <ArrowUpwardIcon fontSize="small" />
                  </Box>
                  <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                    Total Money In
                  </Typography>
                </Box>

                <Typography
                  variant="h5"
                  fontWeight="bold"
                  sx={{
                    my: 2,
                    color: theme.palette.success.main
                  }}
                >
                  {formatCurrency(filteredTransactions.filter(t => t.type === 'credit').reduce((sum, t) => sum + t.amount, 0))}
                </Typography>
              </Paper>
            </Grid>

            {/* Total Debits Card */}
            <Grid item xs={12} sm={6} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  height: '100%',
                  borderRadius: 4,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.15)} 0%, ${alpha(theme.palette.error.main, 0.05)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  '&:hover': {
                    boxShadow: `0 8px 25px ${alpha(theme.palette.error.main, 0.15)}`,
                    transform: 'translateY(-5px)',
                  }
                }}
              >
                <Box display="flex" alignItems="center" mb={2}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: alpha(theme.palette.error.main, 0.2),
                      color: theme.palette.error.main,
                      mr: 2
                    }}
                  >
                    <ArrowDownwardIcon fontSize="small" />
                  </Box>
                  <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                    Total Money Out
                  </Typography>
                </Box>

                <Typography
                  variant="h5"
                  fontWeight="bold"
                  sx={{
                    my: 2,
                    color: theme.palette.error.main
                  }}
                >
                  {formatCurrency(filteredTransactions.filter(t => t.type === 'debit').reduce((sum, t) => sum + t.amount, 0))}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Bank Accounts Section */}
        <Paper sx={{ p: 3, mb: 4, borderRadius: 3, boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5" fontWeight="bold">Bank Accounts</Typography>
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                startIcon={<AddBankIcon />}
                onClick={handleOpenBankDialog}
                size="small"
              >
                Add Account
              </Button>
              <Button
                variant="outlined"
                startIcon={<GetAppIcon />}
                onClick={() => setExportDialogOpen(true)}
                size="small"
                disabled={!bankBalances.length}
              >
                Export Statement
              </Button>
            </Box>
          </Box>

          {bankBalances.length === 0 ? (
            <Box sx={{
              textAlign: 'center',
              py: 6,
              px: 3,
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              borderRadius: 2
            }}>
              <AccountBalanceIcon sx={{ fontSize: 60, color: alpha(theme.palette.primary.main, 0.3), mb: 2 }} />
              <Typography variant="h6" gutterBottom>No Bank Accounts Added</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 450, mx: 'auto' }}>
                Add your bank accounts to track balances, deposits, and withdrawals. This helps you manage your business finances more effectively.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddBankIcon />}
                onClick={handleOpenBankDialog}
                sx={{ backgroundImage: 'var(--primary-gradient)' }}
              >
                Add Your First Bank Account
              </Button>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {/* Flat grid of all bank accounts (3 per row) */}
              {bankBalances.map((account) => (
                <Grid item xs={12} sm={6} md={4} key={`${account.bank}-${account.accountNumber}`}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      height: '100%',
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: `0 5px 15px ${alpha('#0D47A1', 0.1)}`,
                        transform: 'translateY(-3px)',
                      }
                    }}
                  >
                    <Box display="flex" alignItems="center" mb={1}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                          mr: 2
                        }}
                      >
                        {account.accountType === 'savings' ? 'S' :
                          account.accountType === 'current' ? 'C' :
                            account.accountType === 'cc' ? 'CC' : 'OD'}
                      </Box>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">{account.bank}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{
                          display: 'flex',
                          alignItems: 'center',
                          fontSize: '0.75rem'
                        }}>
                          <span style={{ textTransform: 'capitalize' }}>{account.accountType}</span> • {account.accountNumber}
                        </Typography>
                      </Box>
                      {/* Badge for account status */}
                      <Box sx={{ ml: 'auto' }}>
                        <Chip
                          label={account.closingBalance >= 0 ? "Active" : "Overdrawn"}
                          size="small"
                          color={account.closingBalance >= 0 ? "success" : "error"}
                          variant="outlined"
                        />
                      </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Current Balance</Typography>
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        color={account.closingBalance >= 0 ? 'primary.main' : 'error.main'}
                      >
                        {formatCurrency(account.closingBalance)}
                      </Typography>
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Money In</Typography>
                        <Typography variant="body2" color="success.main" fontWeight="medium">
                          +{formatCurrency(account.totalCredits)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Money Out</Typography>
                        <Typography variant="body2" color="error.main" fontWeight="medium">
                          -{formatCurrency(account.totalDebits)}
                        </Typography>
                      </Grid>
                    </Grid>

                    {account.description && (
                      <Typography variant="body2" color="text.secondary" mt={2} sx={{
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap'
                      }}>
                        {account.description}
                      </Typography>
                    )}

                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          const filtered = transactions.filter(
                            t => t.bank === account.bank && t.accountNumber === account.accountNumber
                          );
                          setBankFilter([`${account.bank}-${account.accountNumber}`]);
                          setShowFilters(true);
                        }}
                        sx={{ borderRadius: 4 }}
                      >
                        View Transactions
                      </Button>
                      <Button
                        size="small"
                        onClick={() => handleExportWithBank(`${account.bank}-${account.accountNumber}`)}
                        startIcon={<GetAppIcon fontSize="small" />}
                        sx={{ borderRadius: 4 }}
                      >
                        Export
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              ))}

              {/* Summary Card - Only shown if enabled in widget settings */}
              {isWidgetVisible('bankSummary') && (
                <Grid item xs={12} sm={6} md={4}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      height: '100%',
                      background: `linear-gradient(135deg, ${alpha('#4776E6', 0.08)} 0%, ${alpha('#8E54E9', 0.08)} 100%)`,
                      border: `1px solid ${alpha('#4776E6', 0.1)}`,
                      position: 'relative'
                    }}
                  >
                    {/* Settings control */}
                    <Box sx={{ position: 'absolute', top: 10, right: 10 }}>
                      <Tooltip title="This widget can be disabled in Settings">
                        <IconButton
                          size="small"
                          onClick={() => navigate('/settings')}
                          sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
                        >
                          <SettingsIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    <Box display="flex" alignItems="center" mb={2}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: alpha(theme.palette.info.main, 0.2),
                          color: theme.palette.info.main,
                          mr: 2
                        }}
                      >
                        <AssessmentIcon />
                      </Box>
                      <Typography variant="h6" fontWeight="bold">Summary</Typography>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Total Accounts</Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {bankBalances.length}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Total Balance</Typography>
                        <Typography variant="body1" fontWeight="bold" color="primary.main">
                          {formatCurrency(totalClosingBalance)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Total Money In</Typography>
                        <Typography variant="body1" color="success.main" fontWeight="medium">
                          {formatCurrency(totalCredits)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Total Money Out</Typography>
                        <Typography variant="body1" color="error.main" fontWeight="medium">
                          {formatCurrency(totalDebits)}
                        </Typography>
                      </Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="caption" color="text.secondary">Period</Typography>
                    <Typography variant="body2" fontWeight="medium" mb={2}>
                      {formatDate(startDate || new Date())} - {formatDate(endDate || new Date())}
                    </Typography>

                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<FilterListIcon />}
                      onClick={toggleFilters}
                      size="small"
                      sx={{ mt: 1 }}
                    >
                      {showFilters ? 'Hide Filters' : 'Adjust Filters'}
                    </Button>
                  </Paper>
                </Grid>
              )}
            </Grid>
          )}
        </Paper>

        {/* Transactions Table */}
        <Paper elevation={0} sx={{ borderRadius: 3, boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)', overflow: 'hidden', mb: 4 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredTransactions.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                No transactions found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Add your first bank transaction to start tracking bank activity
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setOpenDialog(true)}
              >
                Add Transaction
              </Button>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{
                    background: (theme) => `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                  }}>
                    <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Bank</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Account</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Reference</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Linked To</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Debit</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Credit</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Balance</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTransactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell>{formatDate(transaction.date)}</TableCell>
                      <TableCell>{transaction.description}</TableCell>
                      <TableCell>{transaction.bank}</TableCell>
                      <TableCell>{transaction.accountNumber}</TableCell>
                      <TableCell>
                        {transaction.chequeNumber ? `Cheque #${transaction.chequeNumber}` : transaction.reference}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={transaction.category}
                          size="small"
                          color="default"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {transaction.linkedEntityId ? (
                          <Chip
                            label={`${transaction.linkedEntityType === 'client' ? 'Client' :
                              transaction.linkedEntityType === 'vendor' ? 'Vendor' :
                                transaction.linkedEntityType === 'expense' ? 'Expense' :
                                  'Unknown'}: ${transaction.linkedEntityName}`}
                            size="small"
                            color={transaction.linkedEntityType === 'client' ? 'success' :
                              transaction.linkedEntityType === 'vendor' ? 'primary' :
                                transaction.linkedEntityType === 'expense' ? 'warning' :
                                  'default'}
                            variant="filled"
                          />
                        ) : (
                          <Tooltip title="Transaction not linked">
                            <Chip
                              icon={<UnlinkedIcon />}
                              label="Not Linked"
                              size="small"
                              color="error"
                              variant="outlined"
                            />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {transaction.type === 'debit' ? formatCurrency(transaction.amount) : ''}
                      </TableCell>
                      <TableCell align="right">
                        {transaction.type === 'credit' ? formatCurrency(transaction.amount) : ''}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(transaction.balance)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {/* Add Transaction Dialog */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Add Bank Transaction</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Transaction Date"
                  value={new Date(newTransaction.date)}
                  onChange={(date) => setNewTransaction({
                    ...newTransaction,
                    date: date ? date.toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
                  })}
                  slotProps={{ textField: { fullWidth: true, margin: "normal" } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Transaction Type</InputLabel>
                  <Select
                    name="type"
                    value={newTransaction.type}
                    onChange={(e) => {
                      handleSelectChange(e);
                      // Clear linked entities when changing transaction type
                      setSelectedClient(null);
                      setSelectedVendor(null);
                      setSelectedExpense(null);
                      setNewTransaction(prev => ({
                        ...prev,
                        linkedEntityType: '',
                        linkedEntityId: '',
                        linkedEntityName: ''
                      }));
                    }}
                    label="Transaction Type"
                  >
                    <MenuItem value="debit">Debit (Money Out)</MenuItem>
                    <MenuItem value="credit">Credit (Money In)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="description"
                  label="Description"
                  margin="normal"
                  value={newTransaction.description}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Bank</InputLabel>
                  <Select
                    name="bank"
                    value={newTransaction.bank}
                    onChange={handleSelectChange}
                    label="Bank"
                    required
                  >
                    {Array.from(new Set(bankAccounts.map(account => account.bankName))).map((bank) => (
                      <MenuItem key={bank} value={bank}>
                        {bank}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal" disabled={!newTransaction.bank}>
                  <InputLabel>Account Number</InputLabel>
                  <Select
                    value={newTransaction.accountNumber}
                    onChange={handleBankAccountChange}
                    label="Account Number"
                    required
                  >
                    {bankAccounts
                      .filter(account => account.bankName === newTransaction.bank)
                      .map((account) => (
                        <MenuItem key={account.accountNumber} value={account.accountNumber}>
                          {account.accountNumber}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="amount"
                  label="Amount"
                  type="number"
                  margin="normal"
                  value={newTransaction.amount || ''}
                  onChange={handleInputChange}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Category</InputLabel>
                  <Select
                    name="category"
                    value={newTransaction.category}
                    onChange={handleSelectChange}
                    label="Category"
                    required
                  >
                    {TRANSACTION_CATEGORIES.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="reference"
                  label="Reference Number (Optional)"
                  margin="normal"
                  value={newTransaction.reference}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="chequeNumber"
                  label="Cheque Number (Optional)"
                  margin="normal"
                  value={newTransaction.chequeNumber}
                  onChange={handleInputChange}
                />
              </Grid>

              {/* Linking section - new fields */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }}>
                  <Chip label={`Link to ${newTransaction.type === 'credit' ? 'Client' : 'Vendor/Expense'}`} />
                </Divider>
              </Grid>

              {newTransaction.type === 'credit' ? (
                // Credit transactions should be linked to clients
                <Grid item xs={12}>
                  <Autocomplete
                    options={clients}
                    getOptionLabel={(option) => option.displayName || option.name || ''}
                    value={selectedClient}
                    onChange={(_, newValue) => handleLinkSelectionChange('client', newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Link to Client"
                        fullWidth
                        required
                        helperText="Select the client this payment is from"
                      />
                    )}
                  />
                </Grid>
              ) : (
                // Debit transactions should be linked to vendors or expenses
                <>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      options={vendors}
                      getOptionLabel={(option) => option.displayName || option.name || ''}
                      value={selectedVendor}
                      onChange={(_, newValue) => {
                        handleLinkSelectionChange('vendor', newValue);
                        // Clear expense selection if a vendor is selected
                        if (newValue) {
                          setSelectedExpense(null);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Link to Vendor"
                          fullWidth
                          helperText="Select if this payment is to a vendor"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      options={expenses}
                      getOptionLabel={(option) => option.displayName || option.name || ''}
                      value={selectedExpense}
                      onChange={(_, newValue) => {
                        handleLinkSelectionChange('expense', newValue);
                        // Clear vendor selection if an expense is selected
                        if (newValue) {
                          setSelectedVendor(null);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Link to Expense"
                          fullWidth
                          helperText="Select if this payment is for an expense"
                        />
                      )}
                    />
                  </Grid>

                  {/* Option to create a new expense */}
                  <Grid item xs={12}>
                    <Box sx={{ mt: 1 }}>
                      <Button
                        startIcon={<NewEntryIcon />}
                        onClick={() => setCreateNewExpense(!createNewExpense)}
                        color="primary"
                        variant="outlined"
                        size="small"
                      >
                        {createNewExpense ? 'Cancel New Expense' : 'Create New Expense'}
                      </Button>
                    </Box>
                  </Grid>

                  {/* New expense form */}
                  {createNewExpense && (
                    <>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          name="name"
                          label="Expense Name"
                          margin="normal"
                          value={newExpense.name}
                          onChange={handleNewExpenseChange}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth margin="normal">
                          <InputLabel>Expense Category</InputLabel>
                          <Select
                            name="category"
                            value={newExpense.category}
                            onChange={(e) => setNewExpense(prev => ({
                              ...prev,
                              category: e.target.value
                            }))}
                            label="Expense Category"
                            required
                          >
                            {TRANSACTION_CATEGORIES
                              .filter(category =>
                                category.includes('Expense') ||
                                category === 'Utilities' ||
                                category === 'Rent' ||
                                category === 'Insurance' ||
                                category === 'Office Supplies' ||
                                category === 'Travel' ||
                                category === 'Marketing'
                              )
                              .map((category) => (
                                <MenuItem key={category} value={category}>
                                  {category}
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          name="description"
                          label="Expense Description"
                          margin="normal"
                          value={newExpense.description}
                          onChange={handleNewExpenseChange}
                          multiline
                          rows={2}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Button
                            onClick={handleCreateNewExpense}
                            variant="contained"
                            color="primary"
                          >
                            Save Expense & Link
                          </Button>
                        </Box>
                      </Grid>
                    </>
                  )}
                </>
              )}

              {/* Display the currently linked entity */}
              {newTransaction.linkedEntityId && (
                <Grid item xs={12}>
                  <Box sx={{
                    p: 2,
                    bgcolor: 'background.default',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    mt: 2
                  }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Linked to {newTransaction.linkedEntityType === 'client' ? 'Client' :
                        newTransaction.linkedEntityType === 'vendor' ? 'Vendor' : 'Expense'}:
                    </Typography>
                    <Typography variant="body1" fontWeight="medium" color="primary">
                      {newTransaction.linkedEntityName}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button
              onClick={handleAddTransaction}
              variant="contained"
              sx={{
                backgroundImage: 'var(--primary-gradient)',
              }}
            >
              Add Transaction
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Bank Account Dialog */}
        <Dialog open={openBankDialog} onClose={handleCloseBankDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Add Bank Account</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="bankName"
                  label="Bank Name"
                  margin="normal"
                  value={newBankAccount.bankName}
                  onChange={handleBankInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="accountNumber"
                  label="Account Number"
                  margin="normal"
                  value={newBankAccount.accountNumber}
                  onChange={handleBankInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Account Type</InputLabel>
                  <Select
                    name="accountType"
                    value={newBankAccount.accountType}
                    onChange={handleAccountTypeChange}
                    label="Account Type"
                  >
                    <MenuItem value="savings">Savings</MenuItem>
                    <MenuItem value="current">Current</MenuItem>
                    <MenuItem value="cc">Cash Credit</MenuItem>
                    <MenuItem value="od">Overdraft</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="balance"
                  label="Opening Balance"
                  type="number"
                  margin="normal"
                  value={newBankAccount.balance || ''}
                  onChange={handleBankInputChange}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="description"
                  label="Description (Optional)"
                  margin="normal"
                  value={newBankAccount.description}
                  onChange={handleBankInputChange}
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseBankDialog}>Cancel</Button>
            <Button
              onClick={handleAddBankAccount}
              variant="contained"
              sx={{
                backgroundImage: 'var(--primary-gradient)',
              }}
            >
              Add Account
            </Button>
          </DialogActions>
        </Dialog>

        {/* Export Bank Selection Dialog */}
        <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Select Bank Account for Export</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" paragraph>
              Select which bank account statement you would like to export. The CSV will include opening balance, transactions, and running balance.
            </Typography>

            <List sx={{ pt: 1 }}>
              <ListItem
                button
                onClick={() => handleExportWithBank('all')}
                sx={{
                  borderRadius: 1,
                  '&:hover': { bgcolor: 'primary.lighter' }
                }}
              >
                <ListItemText
                  primary="All Bank Accounts"
                  secondary="Export combined statement for all accounts"
                />
              </ListItem>

              <Divider sx={{ my: 1 }} />

              {bankBalances.map((account) => (
                <ListItem
                  button
                  key={`${account.bank}-${account.accountNumber}`}
                  onClick={() => handleExportWithBank(`${account.bank}-${account.accountNumber}`)}
                  sx={{
                    borderRadius: 1,
                    '&:hover': { bgcolor: 'primary.lighter' }
                  }}
                >
                  <ListItemText
                    primary={`${account.bank}`}
                    secondary={`Account: ${account.accountNumber} - Balance: ${formatCurrency(account.closingBalance)}`}
                  />
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setExportDialogOpen(false)}>
              Cancel
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add a SpeedDial floating action button with shining animation */}
        <SpeedDial
          ariaLabel="Create new entry"
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1050,
          }}
          icon={<SpeedDialIcon />}
          onClose={() => setFabOpen(false)}
          onOpen={() => setFabOpen(true)}
          open={fabOpen}
          FabProps={{
            sx: {
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              backgroundSize: '200% 200%',
              animation: `${shineAnimation} 3s ease-in-out infinite`,
              boxShadow: '0 4px 20px rgba(33, 150, 243, 0.3)',
              '&:hover': {
                boxShadow: '0 6px 30px rgba(33, 150, 243, 0.5)',
              },
            },
          }}
        >
          {fabActions.map((action) => (
            <SpeedDialAction
              key={action.name}
              icon={action.icon}
              tooltipTitle={action.name}
              onClick={action.action}
              sx={{
                '& .MuiSpeedDialAction-staticTooltipLabel': {
                  minWidth: 120,
                  textAlign: 'center',
                },
              }}
            />
          ))}
        </SpeedDial>
      </Box>
    </AnimatedPage>
  );
};

export default BankBook; 
